import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Radio, Heart, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { publicApi } from '@/lib/api';
import { NavItem } from '@/types';
import { cn, formatStationDateTime, STATION_TIMEZONE } from '@/lib/utils';
import { APP_VERSION } from '@/version';
import { usePublicSettings } from '@/hooks/usePublicSettings';

const DEFAULT_FOOTER_LINKS_BASE: Pick<NavItem, 'id' | 'label' | 'url'>[] = [
  { id: -1, label: 'Events', url: '/events' },
  { id: -2, label: 'News', url: '/blog' },
  { id: -13, label: 'Newsletter', url: '/newsletter' },
  { id: -10, label: 'Podcast', url: '/podcast' },
  { id: -4, label: 'Photo Gallery', url: '/photos' },
  { id: -3, label: 'Radio Schedule', url: '/radio' },
  { id: -12, label: 'Contact us', url: '/contact' },
];

/** Always show /contact when Admin → Navigation overrides footer quick links (form covers feedback, bugs, ads, etc.). */
const REQUIRED_FOOTER_INTERNAL_PATHS: Pick<NavItem, 'id' | 'label' | 'url'>[] = [
  { id: -12, label: 'Contact us', url: '/contact' },
];

function internalPathname(url: string): string | null {
  const u = url.trim();
  if (/^https?:\/\//i.test(u)) return null;
  const path = (u.startsWith('/') ? u : `/${u}`).split('?')[0].split('#')[0];
  return path.toLowerCase() || null;
}

function footerLinksHaveInternalPath(
  links: Pick<NavItem, 'id' | 'label' | 'url'>[],
  pathname: string
): boolean {
  const want = pathname.toLowerCase();
  return links.some((item) => internalPathname(item.url) === want);
}

function mergeRequiredFooterLinks(
  apiLinks: Pick<NavItem, 'id' | 'label' | 'url'>[]
): Pick<NavItem, 'id' | 'label' | 'url'>[] {
  const merged = [...apiLinks];
  for (const req of REQUIRED_FOOTER_INTERNAL_PATHS) {
    const path = internalPathname(req.url);
    if (path && !footerLinksHaveInternalPath(merged, path)) {
      merged.push(req);
    }
  }
  return merged;
}

function defaultFooterLinks(directoryPublic: boolean): Pick<NavItem, 'id' | 'label' | 'url'>[] {
  if (!directoryPublic) return [...DEFAULT_FOOTER_LINKS_BASE];
  return [
    ...DEFAULT_FOOTER_LINKS_BASE.slice(0, 4),
    { id: -5, label: 'Directory', url: '/directory' },
    ...DEFAULT_FOOTER_LINKS_BASE.slice(4),
  ];
}

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

export function Footer() {
  const deploymentColor = (import.meta.env.VITE_DEPLOYMENT_COLOR || 'unknown').toLowerCase();
  const deploymentDotClass = deploymentColor === 'blue'
    ? 'bg-blue-400'
    : deploymentColor === 'green'
      ? 'bg-green-400'
      : 'bg-gray-500';
  const currentYear = new Date().getFullYear();
  const [phoenixTime, setPhoenixTime] = useState(() => formatStationDateTime());
  const { data: settings } = usePublicSettings();

  const { data: footerLinksRaw = [] } = useQuery({
    queryKey: ['footer-nav'],
    queryFn: async (): Promise<NavItem[]> => {
      const data = (await publicApi.footerNav.getAll()).data;
      return Array.isArray(data) ? data : [];
    },
  });

  useEffect(() => {
    const id = window.setInterval(() => setPhoenixTime(formatStationDateTime()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const cssUrl = settings?.logoFontCssUrl;
    if (cssUrl && typeof document !== 'undefined') {
      const existing = document.getElementById('logo-font-css') as HTMLLinkElement | null;
      if (!existing) {
        const link = document.createElement('link');
        link.id = 'logo-font-css';
        link.rel = 'stylesheet';
        link.href = cssUrl;
        document.head.appendChild(link);
      }
    }
  }, [settings?.logoFontCssUrl]);

  const logoCircleUrl = settings?.logoCircleUrl || null;
  const logoHorizontalUrl = settings?.logoHorizontalUrl || settings?.logoUrl || null;
  const businessDirectoryPublic = settings?.businessDirectoryPublic === true;
  const footerHtml = settings?.footerText?.trim();
  const copyrightLine =
    settings?.copyrightText?.trim() || `© ${currentYear} GayPHX. All rights reserved.`;

  const quickLinks: Pick<NavItem, 'id' | 'label' | 'url'>[] = useMemo(() => {
    if (footerLinksRaw.length > 0) {
      const fromApi = footerLinksRaw.map((item) => ({ id: item.id, label: item.label, url: item.url }));
      return mergeRequiredFooterLinks(fromApi);
    }
    return defaultFooterLinks(businessDirectoryPublic);
  }, [footerLinksRaw, businessDirectoryPublic]);

  return (
    <footer className="bg-secondary-600 border-t border-white/10">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Brand */}
          <div className="lg:col-span-7">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                {logoCircleUrl ? (
                  <img src={logoCircleUrl} alt="GayPHX logo" className="w-full h-full object-contain" crossOrigin="anonymous" />
                ) : (
                  <Radio className="w-4 h-4 text-white" />
                )}
              </div>
              {logoHorizontalUrl ? (
                <img src={logoHorizontalUrl} alt="GayPHX" className="h-7 object-contain" crossOrigin="anonymous" />
              ) : (
                <span className="font-logo text-xl font-bold text-white">
                  Gay<span className="text-primary">PHX</span>
                </span>
              )}
            </Link>
            {footerHtml ? (
              <div
                className="text-gray-400 text-sm max-w-md prose prose-invert prose-sm footer-custom-html"
                dangerouslySetInnerHTML={{ __html: footerHtml }}
              />
            ) : (
              <p className="text-gray-400 text-sm max-w-md">
                Phoenix&apos;s LGBTQ+ Radio Station and Event Hub. Connecting our community through music, events, and
                shared experiences.
              </p>
            )}
          </div>

          {/* Quick Links — multi-column grid so many links stay compact (Admin → Navigation) */}
          <div className="lg:col-span-5">
            <h4 className="font-display font-semibold text-white mb-4">Quick Links</h4>
            <ul
              className={cn(
                'grid gap-x-8 gap-y-2.5 text-sm',
                quickLinks.length > 10 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'
              )}
            >
              {quickLinks.map((item) => (
                <li key={item.id} className="min-w-0">
                  {isExternalUrl(item.url) ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-primary transition-colors block truncate"
                      title={item.label}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.url.startsWith('/') ? item.url : `/${item.url}`}
                      className="text-gray-400 hover:text-primary transition-colors block truncate"
                      title={item.label}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm text-center md:text-left">{copyrightLine}</p>
          <p className="text-gray-500 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-center sm:text-right">
            <span className="text-xs text-gray-500 tabular-nums" title={`Station time (${STATION_TIMEZONE})`}>
              Phoenix: {phoenixTime}
            </span>
            <span className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              <span className="flex items-center gap-2">
                Made with <Heart className="w-4 h-4 text-primary fill-primary shrink-0" /> in Phoenix
              </span>
              <span className="text-gray-600">·</span>
              <a
                href="https://1lpro.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-sm"
              >
                1LPro.com
              </a>
              <span className="text-xs opacity-50">v{APP_VERSION}</span>
              <span
                className={cn('w-2.5 h-2.5 rounded-full ring-1 ring-white/30', deploymentDotClass)}
                title={`Deployment: ${deploymentColor}`}
                aria-label={`Deployment: ${deploymentColor}`}
              />
              <Link
                to="/admin"
                aria-label="Site admin"
                className="text-gray-500 hover:text-primary transition-colors inline-flex"
              >
                <Lock className="w-4 h-4" />
              </Link>
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}
